import { z } from 'zod'

/**
 * AI creative (docs/17-AI-CREATIVE.md): series art — directions an art
 * director's planner chooses, each a main graphic and, on request, its
 * matching slide background — and quote cards from a sermon, set by us over
 * a text-free background. The pure parts, shared by the API and the web.
 */

// ---- series art -----------------------------------------------------------------------------

/** Directions a run makes: three main graphics at a time. */
export const SERIES_ART_DIRECTIONS = 3

/** A run (the planner and its images) or one image. */
export const CREATIVE_STATES = ['processing', 'ready', 'failed'] as const
export type CreativeState = (typeof CREATIVE_STATES)[number]

/** A direction's slide background: none asked for yet, or being made, made, failed. */
export const BACKGROUND_STATES = ['none', 'processing', 'ready', 'failed'] as const
export type BackgroundState = (typeof BACKGROUND_STATES)[number]

/** What a person may add for the planner: a short line ("no blue, warm and hopeful"). */
export const ART_PREFERENCES_MAX = 300

export const artPreferencesSchema = z.object({
  preferences: z
    .string()
    .trim()
    .max(ART_PREFERENCES_MAX, `At most ${ART_PREFERENCES_MAX} characters`),
})
export type ArtPreferencesInput = z.input<typeof artPreferencesSchema>

/** One direction as the planner chooses it. */
export interface ArtDirectionPlan {
  /** "Blueprint of a Kingdom". */
  name: string
  /** Why it fits the series, in one sentence. */
  why: string
  /** It prominently carries a recognizable Christian symbol or object. */
  christianImagery: boolean
  /** It depicts the series' subject directly. */
  directReference: boolean
  /** The prompt for its main graphic (title and all). */
  mainPrompt: string
  /** The prompt for its text-free slide background. */
  backgroundPrompt: string
}

export interface ArtPlan {
  /** The language the series is in (BCP 47, "en", "es"). */
  language: string
  directions: ArtDirectionPlan[]
}

const text = (max: number) => z.string().trim().min(1).max(max)

const artPlanSchema = z.object({
  language: z.string().trim().min(2).max(20),
  directions: z
    .array(
      z.object({
        name: text(80),
        why: text(400),
        christianImagery: z.boolean(),
        directReference: z.boolean(),
        mainPrompt: text(6000),
        backgroundPrompt: text(6000),
      }),
    )
    .min(1),
})

/**
 * The planner's answer, or null when it is not the shape asked for. More than
 * a run's directions: the first ones. Duplicate names (a model repeating
 * itself) are dropped.
 */
export function parseArtPlan(value: unknown): ArtPlan | null {
  const parsed = artPlanSchema.safeParse(value)
  if (!parsed.success) return null
  const seen = new Set<string>()
  const directions = parsed.data.directions.filter((d) => {
    const key = d.name.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  return {
    language: parsed.data.language,
    directions: directions.slice(0, SERIES_ART_DIRECTIONS),
  }
}

/**
 * The owner's two requirements over a set (docs/17): at least one direction
 * with prominent Christian imagery, at least one that depicts the subject.
 * Checked over everything made for the series so far plus the new ones —
 * "3 more" need not repeat them, a first set must have them.
 */
export function meetsArtRequirements(
  directions: readonly Pick<ArtDirectionPlan, 'christianImagery' | 'directReference'>[],
): boolean {
  return directions.some((d) => d.christianImagery) && directions.some((d) => d.directReference)
}

// ---- quote cards ------------------------------------------------------------------------------

export const QUOTE_CARD_KINDS = ['quote', 'point', 'verse'] as const
export type QuoteCardKind = (typeof QUOTE_CARD_KINDS)[number]

/** How a card sets its words. */
export const QUOTE_CARD_LAYOUTS = ['center', 'left', 'band'] as const
export type QuoteCardLayout = (typeof QUOTE_CARD_LAYOUTS)[number]

export const QUOTE_LAYOUT_LABELS: Record<QuoteCardLayout, string> = {
  center: 'Centered',
  left: 'Left',
  band: 'Band',
}

/** Public-domain translations we ship (a verse's wording is never a model's). */
export const SHIPPED_TRANSLATIONS = ['BSB'] as const
export type ShippedTranslation = (typeof SHIPPED_TRANSLATIONS)[number]

/** A card as it is kept and edited. */
export interface QuoteCard {
  kind: QuoteCardKind
  /** The words on the card: a quote, a point, a verse's wording ('' for a verse shown by reference only). */
  text: string
  /** A verse's reference ("John 1:14"); for a quote, who said it (optional). */
  reference: string
  /** The translation a verse's wording is from; null for a reference only or a quote. */
  translation: ShippedTranslation | null
  layout: QuoteCardLayout
}

export const QUOTE_CARDS = 3
export const QUOTE_TEXT_MAX = 600
export const QUOTE_REFERENCE_MAX = 80

/** A card as an Editor saves it (the wording edited before export). */
export const quoteCardSchema = z.object({
  kind: z.enum(QUOTE_CARD_KINDS),
  text: z.string().trim().max(QUOTE_TEXT_MAX, `At most ${QUOTE_TEXT_MAX} characters`),
  reference: z
    .string()
    .trim()
    .max(QUOTE_REFERENCE_MAX, `At most ${QUOTE_REFERENCE_MAX} characters`),
  translation: z.enum(SHIPPED_TRANSLATIONS).nullable(),
  layout: z.enum(QUOTE_CARD_LAYOUTS),
})

/** The model's picks: what each card says, before a verse is looked up. */
export interface QuotePick {
  kind: QuoteCardKind
  /** A quote's or point's words; a verse's is looked up, never the model's. */
  text: string
  reference: string
}

export interface QuotePlan {
  language: string
  cards: QuotePick[]
  /** The background's prompt: text-free, quiet in the middle. */
  backgroundPrompt: string
}

const quotePlanSchema = z.object({
  language: z.string().trim().min(2).max(20),
  cards: z
    .array(
      z.object({
        kind: z.enum(QUOTE_CARD_KINDS),
        text: z.string().trim().max(QUOTE_TEXT_MAX),
        reference: z.string().trim().max(QUOTE_REFERENCE_MAX),
      }),
    )
    .min(1),
  backgroundPrompt: z.string().trim().min(1).max(4000),
})

/** The model's quote-card answer, or null. A quote or point with no words is dropped. */
export function parseQuotePlan(value: unknown): QuotePlan | null {
  const parsed = quotePlanSchema.safeParse(value)
  if (!parsed.success) return null
  const cards = parsed.data.cards
    .filter((card) => (card.kind === 'verse' ? card.reference !== '' : card.text !== ''))
    .slice(0, QUOTE_CARDS)
  if (cards.length === 0) return null
  return { ...parsed.data, cards }
}

/** Is this language English (a verse can be set in the BSB)? "en", "en-GB". */
export const isEnglish = (language: string) => /^en\b/i.test(language.trim())

/** Each card's layout when a set is made: one of each, so the three look like a set. */
export const defaultLayout = (index: number): QuoteCardLayout =>
  QUOTE_CARD_LAYOUTS[index % QUOTE_CARD_LAYOUTS.length] ?? 'center'

/** The sizes a card is exported at. */
export const QUOTE_EXPORT_SIZES = [
  { key: 'slide', label: 'Slide', ratio: '16:9', width: 1920, height: 1080 },
  { key: 'post', label: 'Post', ratio: '1:1', width: 1080, height: 1080 },
  { key: 'story', label: 'Story', ratio: '9:16', width: 1080, height: 1920 },
] as const
export type QuoteExportSize = (typeof QUOTE_EXPORT_SIZES)[number]['key']

/**
 * Light or dark words for a background, from its mean relative luminance
 * (0–1) behind them: dark words over a light area, light words otherwise.
 */
export function textToneFor(luminance: number): 'light' | 'dark' {
  return luminance > 0.55 ? 'dark' : 'light'
}

/** sRGB (0–255) to relative luminance (WCAG). */
export function relativeLuminance(r: number, g: number, b: number): number {
  const lin = (c: number) => {
    const s = c / 255
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
}

/**
 * A card's words in lines no wider than `maxWidth`, as `measure` measures
 * them (a canvas's `measureText` in the browser). A word wider than a line on
 * its own gets a line of its own.
 */
export function wrapWords(
  words: string,
  maxWidth: number,
  measure: (text: string) => number,
): string[] {
  const lines: string[] = []
  for (const paragraph of words.split(/\n+/)) {
    let line = ''
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word
      if (line && measure(next) > maxWidth) {
        lines.push(line)
        line = word
      } else line = next
    }
    if (line) lines.push(line)
  }
  return lines
}

/**
 * The largest font size (from `max` down to `min`, in steps of 2) at which
 * the words fit `maxLines` lines and `maxHeight` (at `lineHeight` × size),
 * with the lines at that size; at `min` it fits as it can.
 */
export function fitText(
  words: string,
  box: { maxWidth: number; maxHeight: number; maxLines: number; lineHeight: number },
  sizes: { max: number; min: number },
  measureAt: (text: string, size: number) => number,
): { size: number; lines: string[] } {
  for (let size = sizes.max; size >= sizes.min; size -= 2) {
    const lines = wrapWords(words, box.maxWidth, (t) => measureAt(t, size))
    if (lines.length <= box.maxLines && lines.length * size * box.lineHeight <= box.maxHeight)
      return { size, lines }
  }
  return {
    size: sizes.min,
    lines: wrapWords(words, box.maxWidth, (t) => measureAt(t, sizes.min)),
  }
}

/** Straight quotes made typographic, as a card sets them: don't → don’t, "light" → “light”. */
export function typographic(text: string): string {
  return text
    .replace(/(^|[\s([{“‘—–-])"/g, '$1“')
    .replace(/"/g, '”')
    .replace(/(^|[\s([{“‘—–-])'/g, '$1‘')
    .replace(/'/g, '’')
}

/** A downloaded file's name: "Sermon on the Mount – Blueprint.png", nothing a file system minds. */
export function downloadName(...parts: string[]): string {
  return parts
    .map((part) =>
      [...part]
        .map((ch) => (/[\\/:*?"<>|]/.test(ch) || ch.charCodeAt(0) < 32 ? ' ' : ch))
        .join('')
        .trim(),
    )
    .filter(Boolean)
    .join(' – ')
    .slice(0, 120)
}
